import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import { historyShape, userShape } from 'constants/propTypes';

import AgentPortalError from 'errors/AgentPortalError';

import { GET_STATIONS } from 'store/queries';

import { useLazyTimetableServicesQuery } from 'components/queries/useTimetableServicesQuery';

import { getDefaultStations } from 'pages/BookingMagnet/BookingMagnet.utils';

import { Spinner } from 'admin-components/components';
import { Column, Row } from 'components/shared/Layout';
import Button, { ActionButton } from 'components/ui/Button';
import Select from 'components/ui/Select';

import { StyledContainer, StyledSearchBar, StyledTrains, StyledInputDate } from './LiveTrainsInformation.styles';

import Train from './Train';
import { parse, stringify as queryStringify } from 'query-string';

const LiveTravelInformation = ({ user, history }) => {
  const {
    location: { search }
  } = history;
  const {
    startDate = null,
    origin: urlOrigin = null,
    destination: urlDestination = null,
    trainNumber: urlTrainNumber = null
  } = parse(search);
  const [origin, setOrigin] = useState(urlOrigin);
  const [destination, setDestination] = useState(urlDestination);
  const [date, setDate] = useState(startDate || new Date(Date.now()));
  const [focusedTrainNumber, setFocusedTrainNumber] = useState(urlTrainNumber);
  const [timetableServicesQuery, { timetableServices, loading: timetableServicesLoading, called: timetableServicesCalled }] =
    useLazyTimetableServicesQuery();
  const { data: allStationsData, loading: allStationsLoading, error: allStationsError } = useQuery(GET_STATIONS);
  const allStations = allStationsData?.stations;

  const [allStationsSorted, setAllStationsSorted] = useState([]);

  const inputFormattedDate = moment(date).format('Y-MM-DD');

  const callTimetableServices = useCallback(() => {
    if (!origin || !destination) {
      return;
    }

    if (origin && destination && inputFormattedDate) {
      const queryString = queryStringify({
        origin,
        destination,
        startDate: inputFormattedDate
      });

      history.replace(`/live-trains?${queryString}`);
    }
    timetableServicesQuery({ date: inputFormattedDate, destinationUic: destination, originUic: origin });
  }, [destination, origin, timetableServicesQuery, inputFormattedDate, history]);

  useEffect(() => {
    if (!allStations) {
      return;
    }

    const sorted = [...allStations].sort((a, b) => a.name.localeCompare(b.name));
    setAllStationsSorted(sorted);
  }, [allStations, setAllStationsSorted]);

  useEffect(() => {
    if (allStationsLoading || !allStations || !allStationsSorted.length) {
      return;
    }

    const defaultStations = getDefaultStations(user, allStations);
    const { origin: defaultOrigin, destination: defaultDestination } = defaultStations;

    const isPossibleOrigin = allStations.some(({ uic }) => String(uic) === defaultOrigin);
    const isPossibleDestination = allStations.some(({ uic }) => String(uic) === defaultDestination);

    const availableOrigin = urlOrigin ? urlOrigin : isPossibleOrigin ? defaultOrigin : allStationsSorted[0].uic;
    const availableDestination = urlDestination ? urlDestination : isPossibleDestination ? defaultDestination : allStationsSorted[0].uic;

    setOrigin(availableOrigin);
    setDestination(availableDestination);
  }, [allStations, allStationsLoading, allStationsSorted, user, urlDestination, urlOrigin]);

  useEffect(() => {
    if (timetableServicesCalled) {
      return;
    }

    callTimetableServices();
  }, [timetableServicesCalled, callTimetableServices]);

  const handleSwapStations = () => {
    setOrigin(destination);
    setDestination(origin);
  };

  const handleSearch = () => {
    setFocusedTrainNumber(null);
    callTimetableServices();
  };

  if (allStationsError) {
    throw new AgentPortalError(allStationsError);
  }

  const changeDate = (value) => {
    const newDate = moment(value, 'Y-MM-DD').toDate();
    setDate(newDate);
  };
  const loading = allStationsLoading || timetableServicesLoading;

  return (
    <StyledContainer>
      <h1>
        <FormattedMessage id="homepage.liveTrains" />
      </h1>
      {loading ? (
        <Row span={16} height="100vh">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      ) : (
        <>
          <StyledSearchBar>
            <Select
              data-testid="live-trains-information-origin"
              value={origin}
              name="origin"
              onChange={({ target: { value } }) => setOrigin(value)}
              disabled={loading}
            >
              {allStationsSorted.map(({ uic, name }) => (
                <option key={uic} value={uic}>
                  {name}
                </option>
              ))}
            </Select>
            <Button
              id="swap-button"
              data-cy="swap-button"
              data-testid="live-trains-information-swap"
              onClick={() => handleSwapStations()}
              type="button"
              icon="HorizontalSwap"
              disabled={!origin || !destination || loading}
            />
            <Select
              data-testid="live-trains-information-destination"
              value={destination}
              name="destination"
              onChange={({ target: { value } }) => setDestination(value)}
              disabled={loading}
            >
              {allStationsSorted.map(({ uic, name }) => (
                <option key={uic} value={uic}>
                  {name}
                </option>
              ))}
            </Select>
            <StyledInputDate
              data-testid="live-trains-information-date"
              value={inputFormattedDate}
              onChange={({ target: { value } }) => changeDate(value)}
            />
            <ActionButton
              data-cy="live-train-search"
              data-testid="live-trains-information-search-button"
              disabled={!origin || !destination || loading}
              onClick={handleSearch}
            >
              <FormattedMessage id="liveTrainsInformation.searchForLiveTrains" />
            </ActionButton>
          </StyledSearchBar>

          {timetableServices && Boolean(timetableServices.length) && (
            <StyledTrains>
              {timetableServices.map((timetableService, index) => {
                const isFocused = focusedTrainNumber != null && String(timetableService?.model?.trainNumber) === String(focusedTrainNumber);

                return <Train key={timetableService?.model?.trainNumber ?? index} {...timetableService} isFocused={isFocused} />;
              })}
            </StyledTrains>
          )}

          {timetableServices && !timetableServices.length && <FormattedMessage id="liveTrainsInformation.noResult" />}
        </>
      )}
    </StyledContainer>
  );
};

LiveTravelInformation.propTypes = {
  user: userShape.isRequired,
  history: historyShape
};

export default LiveTravelInformation;
